import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, InputAdornment, Snackbar, SnackbarContent, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { DEFAULT_ROWS_PER_PAGE, type RowsPerPage } from '../../components/RowsPerPageSelect';
import { fetchDataStatus, type PeDataStatus } from '../ProgramEvaluation/dataStatus';
import { fetchSetupState, settingsConfirmed, type SetupState } from '../../core/setupStateApi';
import { GeneratePlansDialog } from '../Generate/GeneratePlansDialog';
import { APP_LINK_STATE, NAV_ROUTES } from '../Home/navRoutes';
import {
  type RosterClassificationResult,
  type RosterClassificationRow,
  fetchPlanSignOffs,
  type PlanSignOffsResult,
} from './rosterClassification';
import {
  deferredRosterPager,
  localRosterPager,
  serverRosterPager,
  EMPTY_FACETS,
  type RosterPageAnswer,
  type RosterPageContext,
  type RosterPageResult,
  type RosterPageRow,
  type RosterPageSource,
  type RosterQuery,
  type RosterSignOffs,
} from './rosterPage';
import { formatUploadDate, rolledOver } from './lastUpdated';
import { rosterAsOfLabel } from '../../core/rosterMeta';
import { logEvent } from '../../utils/eventLog';
import { classificationToRosterRow, type RosterRow } from './rosterRows';
import { RosterTable } from './RosterTable';
import { FilterBar } from './FilterBar';
import { DimensionPicker } from './DimensionPicker';
import type { FilterChip } from './filters';
import { enumerateValues } from './dimensions';
import { withSuggestion } from './filterSuggestions';
import { evidenceColumnsFor } from './evidenceColumns';
import { rosterCountLine } from './countLine';
import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { usePlansStatus } from '../Plans/plansGenerated';
import { SAMPLE_PLAN_ROUTE } from '../Samples/routes';
import { ROSTER } from './tokens';
import { StudentsBanner } from './StudentsBanner';
import { studentsBannerCopy } from './bannerState';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const TOAST_BG = '#2C2C52';

type Props = {
  classificationSource?: () => Promise<RosterClassificationRow[] | RosterClassificationResult | null>;
  pageSource?: RosterPageSource;
  dataStatusSource?: () => Promise<PeDataStatus>;
  setupStateSource?: () => Promise<SetupState>;
  initialChips?: FilterChip[];
  planSignOffsSource?: () => Promise<PlanSignOffsResult | null>;
};

function EmptyState({ onGoToData }: { onGoToData?: () => void }) {
  return (
    <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
      <Typography
        sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.muted }}
      >
        No students yet. Upload your district’s data to see your EL roster.
      </Typography>
      {onGoToData && (
        <Button
          onClick={onGoToData}
          sx={{ mt: 2, textTransform: 'none', fontFamily: ROSTER.font, fontWeight: 600 }}
        >
          Go to Data & Integrations
        </Button>
      )}
    </Box>
  );
}

function signOffReason(chip: FilterChip | undefined, signOffs: RosterSignOffs | null): string | null {
  if (chip === undefined || !('values' in chip) || !signOffs?.available) return null;
  if (!signOffs.generated && signOffs.statuses === 0) {
    return `Plans for ${signOffs.year} haven’t been generated yet, so no student has a sign-off status.`;
  }
  const asked = enumerateValues('planSignoff', [])
    .filter((option) => chip.values.includes(option.value))
    .map((option) => option.label.toLowerCase())
    .join(' or ');
  return `No ${signOffs.year} plan you can see is ${asked}.`;
}

function NoMatches({ reason }: { reason?: string | null }) {
  return (
    <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
      <Typography
        sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.ink }}
      >
        No students match these filters.
      </Typography>
      <Typography
        sx={{ fontFamily: ROSTER.font, fontSize: 13, lineHeight: '20px', color: ROSTER.muted }}
      >
        {reason ?? 'Remove a chip or adjust a value — the filters stay put.'}
      </Typography>
    </Box>
  );
}

function toRosterRow(row: RosterPageRow, signOffs: RosterSignOffs | null): RosterRow {
  const base = classificationToRosterRow(row);
  return signOffs?.available ? { ...base, planSignOff: row.planSignOff ?? null } : base;
}

export function RosterPage({
  classificationSource,
  pageSource,
  dataStatusSource = fetchDataStatus,
  setupStateSource = fetchSetupState,
  initialChips = [],
  planSignOffsSource = fetchPlanSignOffs,
}: Props = {}) {
  const navigate = useNavigate();
  const { isSuperAdmin, isDistrictAdmin } = useAuth();
  const admin = isSuperAdmin || isDistrictAdmin;
  const [searchParams] = useSearchParams();
  const schoolFilter = searchParams.get('school') ?? '';
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(DEFAULT_ROWS_PER_PAGE);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [chips, setChips] = useState<FilterChip[]>(initialChips);
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const [answer, setAnswer] = useState<RosterPageResult | null | undefined>(undefined);
  const [hydrated, setHydrated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [signOffsVersion, setSignOffsVersion] = useState(0);
  const [dataStatus, setDataStatus] = useState<PeDataStatus | null | undefined>(undefined);
  const [setupState, setSetupState] = useState<SetupState | null | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const plans = usePlansStatus();
  const source = useRef<RosterPageSource | null>(null);
  if (source.current === null) {
    source.current =
      pageSource ??
      (classificationSource === undefined
        ? serverRosterPager
        : deferredRosterPager(classificationSource, planSignOffsSource));
  }

  const rosterQuery = useMemo<RosterQuery>(
    () => ({ page, size: rowsPerPage, sort: sortDirection, q: query, school: schoolFilter, departed: includeDeparted, filters: chips }),
    [page, rowsPerPage, sortDirection, query, schoolFilter, includeDeparted, chips],
  );
  const counts = answer?.counts;
  const signOffs = answer?.signOffs ?? null;
  const rows = useMemo(() => (answer?.students ?? []).map((row) => toRosterRow(row, signOffs)), [answer, signOffs]);
  const facets = answer?.facets ?? EMPTY_FACETS;
  const lastUpdated = answer?.lastUploadedAt ?? null;
  const rosterMeta = answer?.roster ?? null;
  const anyRows = (counts?.all ?? 0) > 0;
  const accessInRows = (counts?.access ?? 0) > 0;
  const accessUploaded: boolean | null = !admin
    ? hydrated && !failed
      ? accessInRows
      : null
    : dataStatus === undefined
      ? null
      : dataStatus === null
        ? hydrated && !failed
          ? accessInRows
          : null
        : dataStatus.accessPresent || accessInRows;
  const banner =
    hydrated && !failed && plans !== null && accessUploaded !== null
      ? studentsBannerCopy({
          admin,
          accessUploaded,
          settingsConfirmed: setupState == null ? null : settingsConfirmed(setupState),
          plansGenerated: plans.generated,
          accessStudentCount: counts?.access ?? 0,
        })
      : null;
  const openStudent = (id: string) => navigate(`/roster/${encodeURIComponent(id)}`);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState<FilterChip | null>(null);

  useEffect(() => {
    if (!admin) return undefined;
    let active = true;
    dataStatusSource()
      .then((status) => {
        if (active) setDataStatus(status);
      })
      .catch(() => {
        if (active) setDataStatus(null);
      });
    setupStateSource()
      .then((state) => {
        if (active) setSetupState(state);
      })
      .catch(() => {
        if (active) setSetupState(null);
      });
    return () => {
      active = false;
    };
  }, [admin, dataStatusSource, setupStateSource]);

  const signOffChip = chips.find((chip) => chip.dimension === 'planSignoff');
  const plansGenerated = plans?.generated === true;
  const [generationsConfirmed, setGenerationsConfirmed] = useState(0);
  const currentSignOffsKey = `${plansGenerated}|${generationsConfirmed}`;
  const [signOffsKey, setSignOffsKey] = useState(currentSignOffsKey);
  if (!dialogOpen && signOffsKey !== currentSignOffsKey) setSignOffsKey(currentSignOffsKey);
  const context = useMemo<RosterPageContext>(
    () => ({ admin, signOffsKey, onSignOffs: () => setSignOffsVersion((version) => version + 1) }),
    [admin, signOffsKey],
  );

  useEffect(() => {
    let active = true;
    const apply = (result: RosterPageAnswer | null) => {
      if (!active) return;
      if (result !== null && 'legacy' in result) {
        source.current = localRosterPager(result.legacy, planSignOffsSource);
        run();
        return;
      }
      setFailed(result === null);
      setAnswer(result);
      setHydrated(true);
    };
    const run = () => {
      let pending: ReturnType<RosterPageSource['page']>;
      try {
        pending = (source.current as RosterPageSource).page(rosterQuery, context);
      } catch {
        pending = null;
      }
      if (pending instanceof Promise) pending.then(apply, () => apply(null));
      else apply(pending);
    };
    run();
    return () => {
      active = false;
    };
  }, [rosterQuery, context, signOffsVersion, planSignOffsSource]);

  // Timed once the hydrated table has committed, so the wait a person saw
  // includes the render, not only the fetch.
  const loadStartedAt = useRef<number | null>(null);
  useEffect(() => {
    if (!hydrated) {
      if (loadStartedAt.current === null) loadStartedAt.current = performance.now();
      return;
    }
    if (loadStartedAt.current === null) return;
    const durationMs = String(Math.max(0, Math.round(performance.now() - loadStartedAt.current)));
    loadStartedAt.current = null;
    logEvent('page.loaded.v1', { page: 'roster', durationMs, status: failed ? 'error' : 'ok' }).catch(() => {});
  }, [hydrated, failed]);

  const severalSchools = (counts?.schools ?? 0) > 1;
  const allDeparted = counts?.allDeparted === true;
  const shown = counts?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(shown / rowsPerPage));

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: ROSTER.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="roster" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '32px 24px' }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', minWidth: 0, flexWrap: 'wrap' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: ROSTER.font,
                fontSize: 28,
                fontWeight: 600,
                lineHeight: '36px',
                color: ROSTER.ink,
              }}
            >
              Students
            </Typography>
            {hydrated && (
              <Typography
                data-testid="roster-count-line"
                sx={{
                  fontFamily: ROSTER.font,
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: '16px',
                  color: ROSTER.muted,
                }}
              >
                {rosterCountLine({
                  shown,
                  total: counts?.scoped ?? 0,
                  filtered: query.trim() !== '' || chips.length > 0,
                  lastUpdated: lastUpdated == null ? null : formatUploadDate(lastUpdated),
                  rosterNote: rosterAsOfLabel(rosterMeta),
                  rolledOver: lastUpdated != null && rolledOver(lastUpdated, new Date()),
                })}
              </Typography>
            )}
          </Box>
          {banner && (
            <StudentsBanner
              copy={banner}
              onSeeSample={() => navigate(SAMPLE_PLAN_ROUTE, { state: { from: 'roster' } })}
              onUploadAccess={admin ? () => navigate(NAV_ROUTES.data) : undefined}
              onGoToDashboard={() => navigate(NAV_ROUTES.dashboard, { state: APP_LINK_STATE })}
              onGeneratePlans={() => setDialogOpen(true)}
            />
          )}
          <Box
            data-testid="roster-card"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              pt: '16px',
              bgcolor: ROSTER.cardBg,
              borderRadius: '12px',
              boxShadow: ROSTER.cardShadow,
            }}
          >
            <Box
              data-testid="roster-controls"
              sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '16px', flexWrap: 'wrap' }}
            >
              {anyRows && (
                <FilterBar
                  chips={chips}
                  onRemove={(index) => {
                    setChips((current) => current.filter((_, i) => i !== index));
                    setPage(1);
                  }}
                  onClearAll={() => {
                    setChips([]);
                    setPage(1);
                  }}
                  onAdd={(anchor) => {
                    setEditing(null);
                    setPickerAnchor(anchor);
                  }}
                  onEdit={(index, anchor) => {
                    setEditing(chips[index] ?? null);
                    setPickerAnchor(anchor);
                  }}
                />
              )}
              <TextField
                size="small"
                placeholder="Search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: ROSTER.muted, width: 24, height: 24 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: '1 1 0',
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px', fontFamily: ROSTER.font, fontSize: 16 },
                  '& input::placeholder': { textOverflow: 'ellipsis' },
                }}
              />
            </Box>
            {signOffChip !== undefined && signOffs?.available === false && (
              <Typography
                data-testid="sign-offs-unavailable"
                sx={{ px: '16px', pb: '8px', fontFamily: ROSTER.font, fontSize: 13, lineHeight: '20px', color: ROSTER.muted }}
              >
                Plan sign-off statuses couldn’t be loaded, so that filter is not applied.
              </Typography>
            )}
            {anyRows && (
              <Box sx={{ px: '16px' }}>
                <Box
                  component="label"
                  data-testid="include-departed"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: 'fit-content',
                    fontFamily: ROSTER.font,
                    fontSize: 13,
                    color: ROSTER.ink,
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    component="input"
                    type="checkbox"
                    checked={includeDeparted}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setIncludeDeparted(e.target.checked);
                      setPage(1);
                    }}
                  />
                  Include departed students
                </Box>
                <DimensionPicker
                  anchorEl={pickerAnchor}
                  open={pickerAnchor !== null}
                  admin={admin}
                  facets={facets}
                  suggest={(q) => (source.current as RosterPageSource).suggest(q, rosterQuery, context)}
                  editing={editing}
                  chips={chips}
                  onClose={() => {
                    setEditing(null);
                    setPickerAnchor(null);
                  }}
                  onPickDimension={() => {}}
                  onPickSuggestion={(suggestion) => {
                    setChips((current) => withSuggestion(current, suggestion));
                    setPage(1);
                    setPickerAnchor(null);
                  }}
                  onPickStudent={(id) => {
                    setPickerAnchor(null);
                    openStudent(id);
                  }}
                  onApply={(chip) => {
                    const sameSlot = (existing: FilterChip) =>
                      existing.dimension === chip.dimension &&
                      (existing as { domain?: string }).domain ===
                        (chip as { domain?: string }).domain;
                    setChips((current) => [...current.filter((e) => !sameSlot(e)), chip]);
                    setEditing(null);
                    setPage(1);
                    setPickerAnchor(null);
                  }}
                />
              </Box>
            )}
            {!anyRows ? (
              !hydrated ? (
                <TableLoading tokens={ROSTER} />
              ) : failed ? (
                <LoadFailure tokens={ROSTER} />
              ) : (
                <EmptyState onGoToData={admin ? () => navigate('/data') : undefined} />
              )
            ) : allDeparted ? (
              <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
                <Typography
                  data-testid="roster-all-departed"
                  sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.ink }}
                >
                  No active students in your most recent dataset.
                </Typography>
                <Typography
                  sx={{ fontFamily: ROSTER.font, fontSize: 13, lineHeight: '20px', color: ROSTER.muted }}
                >
                  Turn on “Include departed students” to see who was here before.
                </Typography>
              </Box>
            ) : shown === 0 && chips.length > 0 ? (
              <NoMatches reason={signOffReason(signOffChip, signOffs)} />
            ) : (
              <RosterTable
                evidenceColumns={evidenceColumnsFor(chips)}
                rows={rows}
                sortDirection={sortDirection}
                onToggleSort={() =>
                  setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
                }
                onOpenRow={openStudent}
                accessUploaded={accessUploaded === true}
                showSchool={severalSchools}
                page={page}
                totalRows={shown}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(next) => {
                  setRowsPerPage(next);
                  setPage(1);
                }}
                onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
                onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            )}
          </Box>
        </Box>
      </Box>
      {admin && (
        <GeneratePlansDialog
          open={dialogOpen}
          studentCount={counts?.access ?? 0}
          onClose={() => setDialogOpen(false)}
          onGenerated={(count) => {
            setDialogOpen(false);
            setGenerationsConfirmed((confirmed) => confirmed + 1);
            setToast(`Plans generated for ${count} students.`);
          }}
        />
      )}
      {toast && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={8000}
          onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        >
          <SnackbarContent
            role="status"
            data-testid="plans-generated-toast"
            message={toast}
            sx={{ bgcolor: TOAST_BG, color: '#FFFFFF', borderRadius: '4px' }}
          />
        </Snackbar>
      )}
    </Box>
  );
}
